// src/pages/commercial/CommercialAvailabilityPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../contexts/AuthContext';

const DAYS = [
    { id: 1, name: 'Lundi' },
    { id: 2, name: 'Mardi' },
    { id: 3, name: 'Mercredi' },
    { id: 4, name: 'Jeudi' },
    { id: 5, name: 'Vendredi' },
    { id: 6, name: 'Samedi' },
    { id: 7, name: 'Dimanche' } // ISO numbering for ease
];

export default function CommercialAvailabilityPage() {
    const { user } = useAuth();
    const [slots, setSlots] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        async function fetchSlots() {
            try {
                const res = await pb.collection('availability_slots').getFullList({
                    filter: `owner = "${user.id}"`
                });
                // Map to object keyed by day_of_week
                const slotMap = {};
                res.forEach(s => {
                    slotMap[s.day_of_week] = s;
                });
                setSlots(slotMap);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        fetchSlots();
    }, [user.id]);

    const handleSave = async (dayId, start, end, enabled) => {
        setSaving(true);
        try {
            const existing = slots[dayId];
            if (!enabled) {
                if (existing) {
                    await pb.collection('availability_slots').delete(existing.id);
                    const newSlots = { ...slots };
                    delete newSlots[dayId];
                    setSlots(newSlots);
                }
            } else {
                if (existing) {
                    await pb.collection('availability_slots').update(existing.id, {
                        start_time: start,
                        end_time: end
                    });
                } else {
                    const created = await pb.collection('availability_slots').create({
                        owner: user.id,
                        day_of_week: dayId,
                        start_time: start,
                        end_time: end
                    });
                    setSlots(prev => ({ ...prev, [dayId]: created }));
                }
            }
        } catch (err) {
            console.error(err);
            alert("Erreur de sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    const copyLink = () => {
        // Assuming the route will be /booking/:userId
        const url = `${window.location.origin}/booking/${user.id}`;
        navigator.clipboard.writeText(url);
        alert(`Lien copié : ${url}`);
    };

    return (
        <CommercialLayout title="Mes Disponibilités 📅">
            <div className="commercial-page-wrapper">
                <style>{`
                .day-row {
                    display: flex; align-items: center; gap: 16px; padding: 16px;
                    background: var(--commercial-panel); border: 1px solid var(--commercial-border);
                    border-radius: 8px; margin-bottom: 12px;
                }
                .day-name { width: 100px; font-weight: 600; }
                .time-input { padding: 8px; border-radius: 6px; border: 1px solid var(--commercial-border); background: var(--commercial-bg); color: var(--commercial-text); }
                `}</style>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                    <div>
                        <h1>Configurer l'Agenda</h1>
                        <p style={{ color: 'var(--commercial-muted)' }}>Définissez vos créneaux pour la page de réservation publique.</p>
                    </div>
                    <button className="btn-primary" onClick={copyLink}>
                        🔗 Copier mon lien de réservation
                    </button>
                </div>

                {loading ? <div>Chargement...</div> : (
                    <div>
                        {DAYS.map(day => {
                            const slot = slots[day.id];
                            const isEnabled = !!slot;
                            return (
                                <DayRow
                                    key={day.id}
                                    day={day}
                                    slot={slot}
                                    onSave={(start, end, enabled) => handleSave(day.id, start, end, enabled)}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}

function DayRow({ day, slot, onSave }) {
    const [enabled, setEnabled] = useState(!!slot);
    const [start, setStart] = useState(slot?.start_time || '09:00');
    const [end, setEnd] = useState(slot?.end_time || '17:00');
    const [dirty, setDirty] = useState(false);

    // Sync if slot changes externally (e.g. after save)
    useEffect(() => {
        setEnabled(!!slot);
        if (slot) {
            setStart(slot.start_time);
            setEnd(slot.end_time);
        }
    }, [slot]);

    const handleChange = () => {
        setDirty(true);
    };

    const handleToggle = (e) => {
        const checked = e.target.checked;
        setEnabled(checked);
        setDirty(true);
    };

    const handleBlur = () => {
        if (dirty) {
            onSave(start, end, enabled);
            setDirty(false);
        }
    };

    return (
        <div className="day-row" style={{ opacity: enabled ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={handleToggle}
                    style={{ width: 20, height: 20, marginRight: 16 }}
                />
                <div className="day-name">{day.name}</div>
            </div>

            {enabled ? (
                <>
                    <input type="time" className="time-input" value={start} onChange={e => { setStart(e.target.value); handleChange(); }} onBlur={handleBlur} />
                    <span>à</span>
                    <input type="time" className="time-input" value={end} onChange={e => { setEnd(e.target.value); handleChange(); }} onBlur={handleBlur} />
                </>
            ) : (
                <span style={{ color: 'var(--commercial-muted)', fontStyle: 'italic' }}>Indisponible</span>
            )}
        </div>
    );
}
